import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentHeadlineVariant } from "scmp-app/components/content/content-headline";
import { MagazinesStyleTagsMap } from "scmp-app/components/content/content-headline/content-headline-tag/variants/magazines-style/const";
import type { contentHeadlineTagContent$key } from "scmp-app/queries/__generated__/contentHeadlineTagContent.graphql";

import { isArticleTypeHidden, useDetermineHeadlineTag } from "./hooks";
import { Flag, FlagDivider } from "./styles";

export type Props = {
  className?: string;
  reference: contentHeadlineTagContent$key;
  useSectionTags?: boolean;
  variant?: ContentHeadlineVariant;
};

export const ContentHeadlineTag: FunctionComponent<Props> = ({
  className,
  reference,
  useSectionTags = false,
  variant,
}) => {
  const content = useFragment(
    graphql`
      fragment contentHeadlineTagContent on Content {
        ...hooksContentHeadlineTagContent
      }
    `,
    reference,
  );
  const { color, entityId, name } = useDetermineHeadlineTag(content, useSectionTags) ?? {};

  if (!name || isArticleTypeHidden(entityId)) return null;

  const getFlagContent = () => {
    switch (variant) {
      case "generic":
        return <>{name}</>;
      case "magazines-style":
        return (
          <>
            {entityId ? MagazinesStyleTagsMap[entityId] : name}
            <FlagDivider> / </FlagDivider>
          </>
        );
      default:
        return (
          <>
            {name}
            <FlagDivider> | </FlagDivider>
          </>
        );
    }
  };

  return (
    <Flag $color={color} $variant={variant} className={className}>
      {getFlagContent()}
    </Flag>
  );
};

ContentHeadlineTag.displayName = "ContentHeadlineTag";
